import { useEffect, useState } from 'react';
import { readWebSession } from '../lib/webSession';

export type TelegramStatus = 'loading' | 'ready' | 'unavailable';

export interface TelegramContext {
  webApp: TelegramWebApp | null;
  status: TelegramStatus;
  ready: boolean;
  initData: string;
  userId: number | null;
  colorScheme: 'light' | 'dark';
}

const MAX_WAIT_MS = 8000;
const POLL_MS = 50;
const SURFACE = '#eeeff4'; // matches the app background so the TG chrome blends

/** The bottom bar is native: we can tint it, never draw into it, so on a
 *  full-bleed screen it has to carry that screen's colour instead of the app
 *  surface. ScreenBackdrop publishes it on `data-tg-bottom-bar` — index.html's
 *  early paint already honours it, and every paint here must too, or the next
 *  viewport event (fullscreen retry, keyboard, rotation) repaints the strip
 *  #eeeff4 under a screen that is an image, which reads as a band. */
function bottomBarColor(): string {
  return document.documentElement.getAttribute('data-tg-bottom-bar') || SURFACE;
}
const MAX_FULLSCREEN_TRIES = 4;

/** Phones only — Desktop/web keep Telegram's default medium panel. */
function isMobileClient(wa: TelegramWebApp): boolean {
  const p = (wa.platform ?? '').toLowerCase();
  return p === 'ios' || p === 'android' || p === 'android_x';
}

/** Chat launches open as a short sheet; expand() grows it; requestFullscreen()
 *  covers the whole TG window. On PC we never enter that mode (and exit if
 *  index.html already requested it). */
function fillScreen(wa: TelegramWebApp) {
  wa.expand();
  if (!isMobileClient(wa)) {
    if (wa.isFullscreen) wa.exitFullscreen?.();
    return;
  }
  if (!wa.isFullscreen) wa.requestFullscreen?.();
}

/** Telegram user id, from `initDataUnsafe` or the signed `initData` string.
 *  `initDataUnsafe.user` is sometimes still empty when the module first runs,
 *  while `initData` is already set — reading only the unsafe object made the
 *  persisted balance cache look like another account and get deleted. */
export function telegramUserId(
  wa: TelegramWebApp | null | undefined = window.Telegram?.WebApp,
): number | null {
  const direct = wa?.initDataUnsafe?.user?.id;
  if (typeof direct === 'number') return direct;
  const initData = wa?.initData;
  if (!initData) return null;
  try {
    const raw = new URLSearchParams(initData).get('user');
    if (!raw) return null;
    const id = (JSON.parse(raw) as { id?: unknown }).id;
    return typeof id === 'number' ? id : null;
  } catch {
    return null;
  }
}

/** Push the device + Telegram-UI safe-area insets into CSS variables so the
 *  whole app can pad content away from the notch / home indicator. */
function applyInsets(wa: TelegramWebApp) {
  const sa = wa.safeAreaInset ?? { top: 0, bottom: 0, left: 0, right: 0 };
  const cs = wa.contentSafeAreaInset ?? { top: 0, bottom: 0, left: 0, right: 0 };
  const root = document.documentElement.style;
  // Top: notch + Telegram header both eat space — keep the sum.
  root.setProperty('--tg-safe-top', `max(${sa.top + cs.top}px, env(safe-area-inset-top, 0px))`);
  // Bottom home-indicator ONLY. Adding contentSafeAreaInset here stacked a
  // fat empty band under the floating nav (the gray strip clients keep
  // circling). Telegram UI clearance is a separate var if a screen needs it.
  root.setProperty(
    '--tg-safe-bottom',
    `max(${sa.bottom}px, env(safe-area-inset-bottom, 0px))`,
  );
  root.setProperty('--tg-content-safe-bottom', `${cs.bottom}px`);
  root.setProperty('--tg-safe-left', `max(${sa.left + cs.left}px, env(safe-area-inset-left, 0px))`);
  root.setProperty(
    '--tg-safe-right',
    `max(${sa.right + cs.right}px, env(safe-area-inset-right, 0px))`,
  );
  // Real webview height in px — body/#root size to this so they never end
  // short of the frame (CSS lvh/vh disagree with Telegram on some phones).
  const stable = wa.viewportStableHeight;
  const live = wa.viewportHeight;
  const px = Math.max(
    typeof stable === 'number' ? stable : 0,
    typeof live === 'number' ? live : 0,
    window.innerHeight || 0,
  );
  if (px > 0) {
    root.setProperty('--tg-viewport-stable-height', `${px}px`);
  }
}

export function useTelegram(): TelegramContext {
  const [ctx, setCtx] = useState<TelegramContext>(() => {
    // Read synchronously on first render — window.Telegram.WebApp is injected
    // by Telegram before the webview script runs, so it's always available at
    // module-eval time. This eliminates 1-2 render cycles before status='ready'.
    const wa = window.Telegram?.WebApp;
    if (wa?.initData) {
      return {
        webApp: wa,
        status: 'ready',
        ready: true,
        initData: wa.initData,
        userId: telegramUserId(wa),
        colorScheme: wa.colorScheme,
      };
    }
    return {
      webApp: null,
      status: 'loading',
      ready: false,
      initData: '',
      userId: null,
      colorScheme: 'light',
    };
  });

  useEffect(() => {
    let cancelled = false;
    let fullscreenBlocked = false;
    let fullscreenTries = 0;
    const start = Date.now();

    function bind(wa: TelegramWebApp) {
      try {
        // Before ready(), so the loading placeholder dismisses into a full screen
        // instead of the compact sheet a chat button opens.
        fillScreen(wa);
        wa.ready();
        // Don't let a vertical swipe collapse/close the app while scrolling.
        wa.disableVerticalSwipes?.();
        // Blend the native chrome with our light surface.
        wa.setHeaderColor?.(SURFACE);
        wa.setBackgroundColor?.(SURFACE);
        wa.setBottomBarColor?.(bottomBarColor());
        wa.MainButton?.hide?.();
        applyInsets(wa);
      } catch {
        // noop — older clients lack some methods
      }
      setCtx({
        webApp: wa,
        status: 'ready',
        ready: true,
        initData: wa.initData,
        userId: telegramUserId(wa),
        colorScheme: wa.colorScheme,
      });
      wa.onEvent('themeChanged', () => {
        setCtx((c) => ({ ...c, colorScheme: wa.colorScheme }));
      });
      // Keep the safe-area vars in sync as fullscreen / orientation changes.
      // requestFullscreen() puts the native bottom bar back to white, so
      // repaint after every inset and viewport change.
      const paintChrome = () => {
        wa.setBackgroundColor?.(SURFACE);
        wa.setHeaderColor?.(SURFACE);
        wa.setBottomBarColor?.(bottomBarColor());
      };
      const onInsets = () => {
        applyInsets(wa);
        paintChrome();
      };
      // Mobile: retry enter fullscreen while the sheet settles.
      // Desktop: retry exitFullscreen if still in API fullscreen.
      const onViewport = (...args: unknown[]) => {
        applyInsets(wa);
        const payload = args[0] as { isStateStable?: boolean } | undefined;
        if (payload?.isStateStable !== false && fullscreenTries < MAX_FULLSCREEN_TRIES) {
          const mobile = isMobileClient(wa);
          const wantEnter = mobile && !fullscreenBlocked && !wa.isFullscreen;
          const wantExit = !mobile && !!wa.isFullscreen;
          if (wantEnter || wantExit) {
            fullscreenTries += 1;
            try {
              fillScreen(wa);
            } catch {
              // older clients
            }
          }
        }
        paintChrome();
      };
      wa.onEvent('fullscreenFailed', () => {
        fullscreenBlocked = true;
      });
      wa.onEvent('safeAreaChanged', onInsets);
      wa.onEvent('contentSafeAreaChanged', onInsets);
      wa.onEvent('fullscreenChanged', onInsets);
      wa.onEvent('viewportChanged', onViewport);
    }

    function tick() {
      if (cancelled) return;
      const wa = window.Telegram?.WebApp;
      if (wa) {
        bind(wa);
        return;
      }
      if (Date.now() - start > MAX_WAIT_MS) {
        setCtx((c) => ({ ...c, status: 'unavailable' }));
        return;
      }
      setTimeout(tick, POLL_MS);
    }

    tick();
    return () => {
      cancelled = true;
    };
  }, []);

  return ctx;
}

/**
 * Authenticated fetch. Inside Telegram the credential is the per-request
 * initData; on the web it is the stored session token.
 */
export function authAuthorizationValue(initData: string): string {
  const webToken = initData ? null : readWebSession();
  return webToken ? `bearer ${webToken}` : `tma ${initData}`;
}

export async function authedFetch(initData: string, input: string, init?: RequestInit) {
  const headers = new Headers(init?.headers);
  headers.set('Authorization', authAuthorizationValue(initData));
  if (init?.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(input, { ...init, headers });
}
